import type { ScheduleEntry } from '@reliance/types/models/Schedule';
import React, { useCallback, useEffect, useState } from 'react';
import Day from './Day';
import {
  SCHEDULE_DAYS,
  type ScheduleBlockCell,
  type ScheduleDayName,
  buildEditModeBlocks,
  calculateScheduleEntries,
} from './scheduleUtils';

export type ScheduleEditorModel = {
  id?: number;
  clientKey?: string;
  type: string;
  entries: ScheduleEntry[] | null | undefined;
};

type TableProps = {
  currentSchedule: ScheduleEditorModel;
  updating?: boolean;
  onUpdateSchedule: (payload: { type: string; entries: { day: number; start: string; stop: string }[] }) => void;
};

const EDIT_MIN_MAX = { min: 0, max: 2400 };

const Table: React.FC<TableProps> = ({ currentSchedule, updating = false, onUpdateSchedule }) => {
  const [blocks, setBlocks] = useState(() => buildEditModeBlocks(currentSchedule.entries, EDIT_MIN_MAX));

  const entriesKey =
    currentSchedule.entries?.map((e) => `${e.day}-${e.start}-${e.stop}`).join('|') ?? '';

  useEffect(() => {
    setBlocks(buildEditModeBlocks(currentSchedule.entries, EDIT_MIN_MAX));
  }, [currentSchedule.id, currentSchedule.clientKey, currentSchedule.type, entriesKey]);

  const setBlockSelected = useCallback((block: ScheduleBlockCell, selected: boolean) => {
    setBlocks((prev) => {
      const dayBlocks = { ...prev[block.day] };
      const prevCell = dayBlocks[block.time];
      if (prevCell.selected === selected) {
        return prev;
      }
      dayBlocks[block.time] = { ...prevCell, selected };
      return { ...prev, [block.day]: dayBlocks };
    });
  }, []);

  const updateSchedule = useCallback(() => {
    const entries = calculateScheduleEntries(blocks as Record<ScheduleDayName, Record<number, ScheduleBlockCell>>);
    onUpdateSchedule({ type: currentSchedule.type, entries });
  }, [blocks, currentSchedule.type, onUpdateSchedule]);

  const colSize = `col-sm-${Math.floor(12 / SCHEDULE_DAYS.length)}`;

  return (
    <div>
      <div className="row g-3">
        {SCHEDULE_DAYS.map((day) => (
          <div key={day} className={`${colSize} px-1`}>
            <Day title={day} blocks={blocks[day]} editMode onBlockSetSelected={setBlockSelected} />
          </div>
        ))}
      </div>
      <div>
        <button className="btn btn-primary" type="button" onClick={updateSchedule} disabled={updating}>
          {updating ? 'Updating...' : 'Update Schedule'}
        </button>
      </div>
    </div>
  );
};

export default Table;
